import { useState } from 'react';

import {
  countersApi,
  type CounterTable,
  type CustomField,
} from '@/api/queries/counters';
import { Button, Icon, Input, Modal, useToast } from '@/components/ui';

interface Props {
  table: CounterTable;
  onClose: () => void;
}

export function CounterSchemaModal({ table, onClose }: Props) {
  const update = countersApi.useUpdateSchema();
  const toast = useToast();
  const [fields, setFields] = useState<CustomField[]>(table.customFields ?? []);

  const addField = () => {
    setFields((prev) => [
      ...prev,
      { key: '', label: '', type: 'string', i18n: false, required: false },
    ]);
  };
  const updateField = (i: number, patch: Partial<CustomField>) => {
    setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  };
  const removeField = (i: number) => {
    setFields((prev) => prev.filter((_, idx) => idx !== i));
  };

  const isDestructive = () => {
    const original = table.customFields ?? [];
    const idx = new Map(fields.map((f) => [f.key, f]));
    for (const o of original) {
      const f = idx.get(o.key);
      if (!f) return true;
      if (f.type !== o.type) return true;
      if (o.i18n && !f.i18n) return true;
    }
    return false;
  };

  const submit = async () => {
    const destructive = isDestructive();
    if (destructive && !confirm('This change drops data from existing rows. Continue?')) {
      return;
    }
    try {
      await update.mutateAsync({
        key: table.key,
        customFields: fields.filter((f) => f.key && f.label),
        confirm: destructive,
      });
      toast.show({ severity: 'success', title: 'Schema updated' });
      onClose();
    } catch (err) {
      toast.show({
        severity: 'error',
        title: err instanceof Error ? err.message : 'Failed to update schema',
      });
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Schema — ${table.name}`}
      subtitle="Default fields (key, value, description, parent) are read-only."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={update.isPending} onClick={submit}>
            Save schema
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div>
          <strong style={{ fontSize: 13 }}>Default fields</strong>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
            key · value (i18n) · description (i18n) · parent
          </div>
        </div>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Custom fields</strong>
            <Button size="sm" variant="ghost" leftIcon={<Icon name="plus" size={12} />} onClick={addField}>
              Add field
            </Button>
          </div>
          {fields.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>None yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {fields.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 120px auto auto auto',
                    gap: 6,
                  }}
                >
                  <Input
                    placeholder="key"
                    value={f.key}
                    onChange={(e) => updateField(i, { key: e.currentTarget.value })}
                  />
                  <Input
                    placeholder="Label"
                    value={f.label}
                    onChange={(e) => updateField(i, { label: e.currentTarget.value })}
                  />
                  <select
                    className="ds-input"
                    value={f.type}
                    onChange={(e) =>
                      updateField(i, { type: e.currentTarget.value as CustomField['type'] })
                    }
                  >
                    <option value="string">string</option>
                    <option value="number">number</option>
                    <option value="boolean">boolean</option>
                    <option value="date">date</option>
                    <option value="reference">reference</option>
                  </select>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={f.i18n}
                      onChange={(e) => updateField(i, { i18n: e.currentTarget.checked })}
                    />
                    i18n
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <input
                      type="checkbox"
                      checked={f.required}
                      onChange={(e) => updateField(i, { required: e.currentTarget.checked })}
                    />
                    required
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => removeField(i)} aria-label="Remove">
                    <Icon name="trash" size={12} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
